import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FontRenderItem,
  SUBTITLE_APPEARANCE_PRESET_IDS,
  SubtitleAppearancePresetId,
  SubtitleStyle,
  getSubtitleAppearancePreset,
  getSubtitleAppearancePresetId,
  resolveSubtitleAppearance,
} from '@packages/global';
import FormModal from '../common/FormModal';
import { videoExportApi } from '../../services/user/videoExportApi';

interface FontAppearanceDialogProps {
  episodeId?: string;
  isOpen: boolean;
  title: string;
  sampleText?: string;
  style: SubtitleStyle;
  onClose: () => void;
  onConfirm: (style: SubtitleStyle) => void;
}

export default function FontAppearanceDialog({
  episodeId,
  isOpen,
  title,
  sampleText,
  style,
  onClose,
  onConfirm,
}: FontAppearanceDialogProps) {
  const { t } = useTranslation(['editor', 'common']);
  const [availableFonts, setAvailableFonts] = useState<FontRenderItem[]>([]);
  const [isLoadingFonts, setIsLoadingFonts] = useState(false);
  const [previewFontFamily, setPreviewFontFamily] = useState('');
  const [draftStyle, setDraftStyle] = useState<SubtitleStyle>(style);

  useEffect(() => {
    if (!isOpen) return;
    setDraftStyle(style);
  }, [isOpen, style]);

  useEffect(() => {
    if (!isOpen || !episodeId) return;

    let cancelled = false;
    setIsLoadingFonts(true);

    videoExportApi.getAvailableFonts(episodeId)
      .then((fonts) => {
        if (!cancelled) {
          setAvailableFonts(fonts);
        }
      })
      .catch((err) => {
        console.error('Failed to load subtitle fonts:', err);
        if (!cancelled) {
          setAvailableFonts([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingFonts(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [episodeId, isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const fontUrl = draftStyle.fontUrl;
    const fontKey = draftStyle.fontId || draftStyle.fontName || fontUrl;

    if (!fontUrl || !fontKey) {
      setPreviewFontFamily('');
      return;
    }

    const family = `VideoAgentFontAppearance-${String(fontKey).replace(/[^a-zA-Z0-9_-]/g, '-')}`;
    let cancelled = false;

    const loadFont = async () => {
      try {
        const fontFace = new FontFace(family, `url(${fontUrl})`);
        await fontFace.load();
        document.fonts.add(fontFace);
        if (!cancelled) {
          setPreviewFontFamily(family);
        }
      } catch (err) {
        console.error('Failed to load preview subtitle font:', err);
        if (!cancelled) {
          setPreviewFontFamily('');
        }
      }
    };

    loadFont();

    return () => {
      cancelled = true;
    };
  }, [draftStyle.fontId, draftStyle.fontName, draftStyle.fontUrl, isOpen]);

  const appearance = useMemo(
    () => resolveSubtitleAppearance(draftStyle, draftStyle.fontSize || 26),
    [draftStyle],
  );

  const buildTemplateTextShadow = (nextAppearance: ReturnType<typeof resolveSubtitleAppearance>) => {
    const outlineColor = nextAppearance.outlineColor;
    const normalized = outlineColor.replace('#', '');
    const full = normalized.length === 3
      ? normalized.split('').map((char) => char + char).join('')
      : normalized;
    const colorInt = parseInt(full, 16);
    const r = (colorInt >> 16) & 255;
    const g = (colorInt >> 8) & 255;
    const b = colorInt & 255;
    const unit = 0.35;

    return [
      `-${unit}px -${unit}px 0 rgba(${r},${g},${b},0.42)`,
      `${unit}px -${unit}px 0 rgba(${r},${g},${b},0.42)`,
      `-${unit}px ${unit}px 0 rgba(${r},${g},${b},0.42)`,
      `${unit}px ${unit}px 0 rgba(${r},${g},${b},0.42)`,
      nextAppearance.shadow > 0 ? `0 0.75px 1.5px rgba(${r},${g},${b},0.12)` : '',
    ].filter(Boolean).join(', ');
  };

  const handleFontChange = (fontId: string) => {
    const selectedFont = availableFonts.find((font) => font.id === fontId);
    setDraftStyle((prev) => ({
      ...prev,
      fontId: selectedFont?.id,
      fontName: selectedFont?.name,
      fontUrl: selectedFont?.renderValue,
    }));
  };

  const handlePresetChange = (presetId: SubtitleAppearancePresetId) => {
    const preset = getSubtitleAppearancePreset(presetId);
    setDraftStyle((prev) => ({
      ...prev,
      appearancePreset: presetId,
      color: preset.textColor,
    }));
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="lg"
      footer={(
        <>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-bg-elevated text-gray-300 hover:text-gray-50 transition-colors"
          >
            {t('common:cancel')}
          </button>
          <button
            onClick={() => onConfirm(draftStyle)}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-500 transition-colors"
          >
            {t('common:confirm')}
          </button>
        </>
      )}
    >
      <div className="space-y-4">
        <div>
          <label className="mb-2 block text-sm text-gray-300">
            {t('editor:subtitleSettings.font')}
          </label>
          <select
            value={draftStyle.fontId || ''}
            onChange={(e) => handleFontChange(e.target.value)}
            disabled={isLoadingFonts}
            className="w-full rounded-lg border border-border-subtle bg-bg-elevated px-3 py-2 text-sm text-gray-100 outline-none transition-colors disabled:cursor-not-allowed disabled:opacity-60"
          >
            <option value="">{t('editor:subtitleSettings.defaultFont')}</option>
            {availableFonts.map((font) => (
              <option key={font.id} value={font.id}>{font.name}</option>
            ))}
          </select>
        </div>

        <div>
          <div className="mb-2 text-sm text-gray-300">
            {t('editor:subtitleSettings.template')}
          </div>
          <div className="grid grid-cols-5 gap-1.5">
            {SUBTITLE_APPEARANCE_PRESET_IDS.map((presetId) => {
              const active = getSubtitleAppearancePresetId(draftStyle) === presetId;
              const preset = getSubtitleAppearancePreset(presetId);
              const previewFontSize = Math.max(24, Math.round((draftStyle.fontSize || 26) * 0.8));
              const presetAppearance = resolveSubtitleAppearance(
                { ...draftStyle, appearancePreset: presetId, color: preset.textColor },
                previewFontSize,
              );
              return (
                <button
                  key={presetId}
                  type="button"
                  onClick={() => handlePresetChange(presetId)}
                  title={t(`editor:subtitleSettings.presets.${presetId}.name`)}
                  className={`h-16 rounded-lg border transition-all ${
                    active
                      ? 'border-cyan-400 scale-[1.03] shadow-[0_0_0_3px_rgba(34,211,238,0.28)]'
                      : 'border-transparent hover:border-border-default hover:scale-[1.02]'
                  }`}
                  style={{ backgroundColor: preset.previewSurface }}
                >
                  <div className="flex h-full items-center justify-center">
                    <span
                      className="inline-block whitespace-nowrap font-bold leading-none"
                      style={{
                        color: presetAppearance.textColor,
                        textShadow: buildTemplateTextShadow(presetAppearance),
                        fontFamily: previewFontFamily || 'inherit',
                        fontSize: `${previewFontSize}px`,
                        lineHeight: 1,
                      }}
                    >
                      字幕
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <div className="mb-2 text-sm text-gray-300">{t('editor:subtitleSettings.preview')}</div>
          <div
            className="rounded-xl border border-border-subtle px-4 py-5 text-center"
            style={{ backgroundColor: appearance.previewSurface }}
          >
            <div
              className="inline-block whitespace-pre-wrap break-words font-bold leading-[1.25]"
              style={{
                color: appearance.textColor,
                textShadow: appearance.cssTextShadow,
                fontFamily: previewFontFamily || 'inherit',
                fontSize: `${Math.max(24, draftStyle.fontSize || 26)}px`,
              }}
            >
              {sampleText || t('editor:subtitleSettings.previewText')}
            </div>
          </div>
        </div>
      </div>
    </FormModal>
  );
}
